<script>
    import { createEventDispatcher } from 'svelte';
    import { slide } from 'svelte/transition';
    import { createEditorAdapter } from '../../utils/editorFormatAdapter.js';
    import { cursorStore } from '../../stores/editorsStore.js';
    
    const dispatch = createEventDispatcher();
    
    // Accept both editors
    export let specEditor = null;
    export let claimsEditor = null;
    
    let showDropdown = false;
    let dropdownElement;
    let currentFocusIndex = -1;
    
    // Create adapters for both editors when they're ready
    $: specAdapter = (specEditor && specEditor.can) ? createEditorAdapter(specEditor, 'tiptap') : null;
    $: claimsAdapter = (claimsEditor && claimsEditor.state) ? createEditorAdapter(claimsEditor, 'codemirror') : null;
    
    
    
    // Get active editor based on cursor position (default to spec if not set)
    $: activeEditor = $cursorStore.activeEditor || 'spec';
    $: activeAdapter = activeEditor === 'claims' ? claimsAdapter : specAdapter;
    
    // Get available formats for active editor
    $: availableFormats = activeAdapter?.getAvailableFormats() || [];
    
    // Spec editor formatting state (only check when spec is active)
    $: canItalic = (activeEditor === 'spec' && specAdapter?.canFormat('italic')) || false;
    $: canUnderline = (activeEditor === 'spec' && specAdapter?.canFormat('underline')) || false;
    $: isItalic = (activeEditor === 'spec' && specAdapter?.isActive('italic')) || false;
    $: isUnderline = (activeEditor === 'spec' && specAdapter?.isActive('underline')) || false;
    
    
    // Shared formatting state (both editors support these)
    $: canSubscript = activeAdapter?.canFormat('subscript') || false;
    $: canSuperscript = activeAdapter?.canFormat('superscript') || false;
    $: isSubscript = activeAdapter?.isActive('subscript') || false;
    $: isSuperscript = activeAdapter?.isActive('superscript') || false;
    
    
    // Check if baseline is needed (either subscript or superscript is active)
    $: needsBaseline = isSubscript || isSuperscript;
    
    // Check if any formatting is active
    $: hasActiveFormatting = isItalic || isUnderline || isSubscript || isSuperscript;
    
    // Determine button state based on editor availability
    $: isDisabled = !specEditor && !claimsEditor;
    
    function toggleDropdown() {
        showDropdown = !showDropdown;
        if (showDropdown) {
            currentFocusIndex = -1;
            // Force re-evaluation of reactive statements after a tick
            // This ensures selection tracking has time to update
            setTimeout(() => {
                canSubscript = activeAdapter?.canFormat('subscript') || false;
                canSuperscript = activeAdapter?.canFormat('superscript') || false;
            }, 0);
        }
    }
    
    function handleFormat(format) {
        if (!activeAdapter) return;
        
        if (format === 'baseline' || format === 'normal') {
            activeAdapter.clearFormatting();
        } else {
            activeAdapter.toggleFormat(format);
        }
        
        // Keep dropdown open after formatting
        // showDropdown = false;
    }
    
    function handleClickOutside(event) {
        if (!event.target.closest('.text-formatter-container')) {
            showDropdown = false;
        }
    }
    
    function handleKeydown(e) {
        if (e.key === 'Escape') {
            showDropdown = false;
            return;
        }
        
        // Handle keyboard navigation when dropdown is open
        if (showDropdown && dropdownElement) {
            const menuItems = dropdownElement.querySelectorAll('[role="menuitem"]:not([disabled])');
            
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                currentFocusIndex = Math.min(currentFocusIndex + 1, menuItems.length - 1);
                if (menuItems[currentFocusIndex]) {
                    menuItems[currentFocusIndex].focus();
                }
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                currentFocusIndex = Math.max(currentFocusIndex - 1, 0);
                if (menuItems[currentFocusIndex]) {
                    menuItems[currentFocusIndex].focus();
                }
            } else if (e.key === 'Tab') {
                // Let Tab work within the menu
                if (currentFocusIndex === -1) {
                    currentFocusIndex = 0;
                    if (menuItems[currentFocusIndex]) {
                        menuItems[currentFocusIndex].focus();
                    }
                }
            }
        }
    }
</script>

<svelte:window on:click={handleClickOutside} on:keydown={handleKeydown} />

<div class="text-formatter-container relative" class:spec-active={activeEditor === 'spec'} class:claims-active={activeEditor === 'claims'}>
    <button 
        id="text-formatter-button"
        class="toolbar-button text-formatter-button flex flex-center gap-2 cursor-pointer"
        class:active={hasActiveFormatting || showDropdown}
        class:spec-active={activeEditor === 'spec'}
        class:claims-active={activeEditor === 'claims'}
        on:click={toggleDropdown}
        title="Text formatting"
        aria-label="Text formatting"
        aria-expanded={showDropdown}
        aria-haspopup="true"
        disabled={isDisabled}
    >
        <i class="fas fa-font"></i>
        <span class="button-text">Text</span>
    </button>
    
    {#if showDropdown && (specAdapter || claimsAdapter)}
        <div 
            class="formatter-dropdown absolute" 
            transition:slide={{ duration: 200 }}
            role="menu"
            aria-labelledby="text-formatter-button"
            tabindex="-1"
            bind:this={dropdownElement}
        >
            {#if activeEditor === 'spec'}
                <div class="dropdown-section">
                    <div class="section-header">Specification</div>
                    
                    <button 
                        class="format-option flex flex-align-center gap-3 w-full cursor-pointer"
                        class:active={isItalic}
                        on:click={() => handleFormat('italic')}
                        disabled={!canItalic}
                        role="menuitem"
                        title="Italic (Ctrl+I)"
                    >
                        <i class="fas fa-italic"></i>
                        <span>Italic</span>
                        <span class="shortcut">ctrl + i</span>
                    </button>
                    
                    <button 
                        class="format-option flex flex-align-center gap-3 w-full cursor-pointer"
                        class:active={isUnderline}
                        on:click={() => handleFormat('underline')}
                        disabled={!canUnderline}
                        role="menuitem"
                        title="Underline (Ctrl+U)"
                    >
                        <i class="fas fa-underline"></i>
                        <span>Underline</span>
                        <span class="shortcut">ctrl + u</span>
                    </button>
                    
                    <div class="divider w-full"></div>
                </div>
            {:else if activeEditor === 'claims'}
                <div class="dropdown-section">
                    <div class="section-header">Claims</div>
                </div>
            {/if}
            
            <!-- Shared formatting options -->
            <button 
                class="format-option flex flex-align-center gap-3 w-full cursor-pointer"
                class:active={isSubscript}
                on:click={() => handleFormat('subscript')}
                disabled={!canSubscript}
                role="menuitem"
                title="Subscript (Ctrl+↓)"
            >
                <span class="format-icon">X<sub>2</sub></span>
                <span>Subscript</span>
                <span class="shortcut">ctrl + ↓</span>
            </button>
            
            <button 
                class="format-option flex flex-align-center gap-3 w-full cursor-pointer"
                class:active={isSuperscript}
                on:click={() => handleFormat('superscript')}
                disabled={!canSuperscript}
                role="menuitem"
                title="Superscript (Ctrl+↑)"
            >
                <span class="format-icon">X<sup>2</sup></span>
                <span>Superscript</span>
                <span class="shortcut">ctrl + ↑</span>
            </button>
            
            <button 
                class="format-option flex flex-align-center gap-3 w-full cursor-pointer"
                on:click={() => handleFormat('normal')}
                role="menuitem"
                title="Normal"
            >
                <span class="format-icon">X</span>
                <span>Normal</span>
            </button>
        </div>
    {/if}
</div>

<style>
    /* Button styling that changes based on active editor */
    .text-formatter-button {
        /* Default neutral styling */
        background-color: var(--color-btn-default);
    }
    
    .text-formatter-button:hover:not(:disabled) {
        background-color: var(--color-btn-default-hover);
    }
    
    /* When spec editor is active */
    .text-formatter-button.spec-active {
        background-color: var(--color-btn-spec);
    }
    
    .text-formatter-button.spec-active:hover:not(:disabled) {
        background-color: var(--color-btn-spec-hover);
    }
    
    .text-formatter-button.spec-active.active {
        background-color: var(--color-btn-spec-active);
    }
    
    /* When claims editor is active (neutral colors) */
    .text-formatter-button.claims-active {
        background-color: var(--color-btn-default);
    }
    
    .text-formatter-button.claims-active:hover:not(:disabled) {
        background-color: var(--color-btn-default-hover);
    }
    
    .text-formatter-button.claims-active.active {
        background-color: var(--color-btn-default-active);
    }
    
    .formatter-dropdown {
        top: calc(100% + 0.5rem);
        left: 0;
        z-index: var(--z-index-dropdown);
        min-width: 200px;
        padding: 0.5rem;
        background-color: var(--color-bg-primary);
        border-radius: 18px;
        box-shadow: var(--shadow-md);
    }
    
    /* Spec-themed dropdown when spec editor is active */
    .text-formatter-container.spec-active .formatter-dropdown {
        background-color: var(--color-bg-spec);
    }
    
    /* Neutral-themed dropdown when claims editor is active */
    .text-formatter-container.claims-active .formatter-dropdown {
        background-color: var(--color-bg-file-dropdown);
    }
    
    .dropdown-section {
        margin-bottom: 0.25rem;
    }
    
    .section-header {
        padding: 0.25rem 1rem;
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        color: var(--color-text-tertiary);
        letter-spacing: 0.05em;
    }
    
    .format-option {
        padding: 0.5rem 1rem;
        font-size: 14px;
        text-align: left;
        white-space: nowrap;
        color: var(--color-text-secondary);
        background: transparent;
        border: none;
        border-radius: 12px;
        transition: all 0.2s ease;
    }
    
    .format-option:hover:not(:disabled) {
        background-color: var(--color-bg-hover-light);
    }
    
    /* Spec-themed hover for spec editor */
    .text-formatter-container.spec-active .format-option:hover:not(:disabled) {
        background-color: var(--color-btn-spec-hover);
    }
    
    .format-option:disabled {
        opacity: var(--opacity-disabled);
        cursor: not-allowed;
    }
    
    .format-option.active {
        color: var(--color-text-primary);
        background-color: var(--color-btn-default-active);
    }
    
    /* Spec-themed active state for spec editor */
    .text-formatter-container.spec-active .format-option.active {
        background-color: var(--color-btn-spec-active);
    }
    
    .format-option i {
        width: 16px;
        text-align: center;
    }
    
    .format-icon {
        width: 16px;
        font-family: 'Times New Roman', serif;
        font-size: 16px;
        text-align: center;
    }
    
    .shortcut {
        margin-left: auto;
        font-size: 12px;
        color: var(--color-text-tertiary);
    }
    
    .divider {
        height: 1px;
        margin: 0.25rem 0;
        background-color: var(--border-color-default);
    }
    
    /* Responsive design handled by parent toolbar styles */
    @media (width <= 768px) {
        .formatter-dropdown {
            min-width: 180px;
        }
        
        .format-option {
            padding: 0.4rem 0.8rem;
            font-size: 13px;
        }
    }
</style>